import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { s8, colors, breakpoints, space, radius, r8, s64, s24, s16, s32, r4 } from "@ardoq/design-tokens";
import { SvgImage, SvgImageName } from "@ardoq/icons";
import styled, { css } from "styled-components";
import { forwardRef } from "react";
import facepaint from "facepaint";
import { Text, Header1, Header5, text1, Header4 } from "@ardoq/typography";
import { ArdoqLoaderComponent, LoaderColor, LoaderSize } from "@ardoq/ardoq-loader-component";
const LoadingScreenLogo = ({
  svgImageName,
  height,
  style,
  width,
  color
}) => /* @__PURE__ */ jsx(
  SvgImage,
  {
    svgImageName: svgImageName ?? SvgImageName.ARDOQ_LOGO_WITH_NAME,
    style: {
      color: color ?? colors.iconOnInvertedStrong,
      width: width ?? 94,
      height: height ?? 72,
      marginTop: s8,
      position: "absolute",
      ...style
    }
  }
);
function isResponsiveCondition(value) {
  return value instanceof Object && value.mobile !== void 0 || value.tablet !== void 0 || value.desktop !== void 0 || value.desktopLarge !== void 0 || value.desktopXLarge !== void 0;
}
function resolveResponsiveProp(prop, resolveValue = (s) => s) {
  if (!prop) return void 0;
  if (isResponsiveCondition(prop)) {
    return [
      prop.mobile && resolveValue(prop.mobile) || null,
      prop.tablet && resolveValue(prop.tablet) || null,
      prop.desktop && resolveValue(prop.desktop) || null,
      prop.desktopLarge && resolveValue(prop.desktopLarge) || null,
      prop.desktopXLarge && resolveValue(prop.desktopXLarge) || null
    ];
  }
  return resolveValue(prop);
}
const mq = facepaint([
  `@media(min-width: ${breakpoints.tablet})`,
  `@media(min-width: ${breakpoints.desktop})`,
  `@media(min-width: ${breakpoints.desktopLarge})`,
  `@media(min-width: ${breakpoints.desktopXLarge})`
]);
const spaceVariantToValue = (spaceVariant) => {
  if (!spaceVariant) return void 0;
  return resolveResponsiveProp(spaceVariant, (spaceVariant2) => {
    return space[spaceVariant2];
  });
};
const marginVariantToValue = (marginVariant) => {
  if (!marginVariant) return void 0;
  return resolveResponsiveProp(marginVariant, (marginVariant2) => {
    return marginVariant2 === "auto" ? "auto" : space[marginVariant2];
  });
};
const widthVariantToValue = (width) => {
  if (!width) return void 0;
  return width === "full" ? "100%" : "fit-content";
};
const resolveCommonBoxProps = ({
  padding,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  paddingX,
  paddingY,
  margin,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  marginX,
  marginY,
  isolate,
  position,
  width,
  height,
  backgroundColor,
  borderColor,
  borderRadius,
  flexGrow,
  flexShrink,
  maxWidth,
  minWidth,
  minHeight,
  DEPRECATED_onClick
}) => {
  const resolvedPaddingLeft = spaceVariantToValue(
    paddingLeft || paddingX || padding
  );
  const resolvedPaddingRight = spaceVariantToValue(
    paddingRight || paddingX || padding
  );
  const resolvedPaddingTop = spaceVariantToValue(
    paddingTop || paddingY || padding
  );
  const resolvedPaddingBottom = spaceVariantToValue(
    paddingBottom || paddingY || padding
  );
  const resolvedMarginLeft = marginVariantToValue(
    marginLeft || marginX || margin
  );
  const resolvedMarginRight = marginVariantToValue(
    marginRight || marginX || margin
  );
  const resolvedMarginTop = marginVariantToValue(
    marginTop || marginY || margin
  );
  const resolvedMarginBottom = marginVariantToValue(
    marginBottom || marginY || margin
  );
  const resolvedWidth = widthVariantToValue(width);
  const resolvedHeight = height === "full" ? "100%" : void 0;
  return {
    $paddingLeft: resolvedPaddingLeft,
    $paddingRight: resolvedPaddingRight,
    $paddingTop: resolvedPaddingTop,
    $paddingBottom: resolvedPaddingBottom,
    $marginLeft: resolvedMarginLeft,
    $marginRight: resolvedMarginRight,
    $marginTop: resolvedMarginTop,
    $marginBottom: resolvedMarginBottom,
    $width: resolvedWidth,
    $height: resolvedHeight,
    $backgroundColor: backgroundColor,
    $borderColor: borderColor,
    $borderRadius: borderRadius,
    $isolate: isolate,
    $position: resolveResponsiveProp(position),
    $flexGrow: resolveResponsiveProp(flexGrow == null ? void 0 : flexGrow.toString()),
    $flexShrink: resolveResponsiveProp(flexShrink == null ? void 0 : flexShrink.toString()),
    $maxWidth: resolveResponsiveProp(maxWidth),
    $minWidth: resolveResponsiveProp(minWidth),
    $minHeight: resolveResponsiveProp(minHeight),
    onClick: DEPRECATED_onClick
  };
};
const StyledBox = styled.div`
  ${({ as }) => (
  // This should typically be reset by the apps, but currently, ardoq-front does not handle this reset, so we've added a reset here to avoid any issues.
  (as === "ul" || as === "ol") && css`
      padding: 0;
      & :where(li) {
        list-style-type: none;
      }
    `
)}

  ${(props) => mq({
  marginTop: props.$marginTop,
  marginBottom: props.$marginBottom,
  marginLeft: props.$marginLeft,
  marginRight: props.$marginRight,
  paddingTop: props.$paddingTop,
  paddingBottom: props.$paddingBottom,
  paddingLeft: props.$paddingLeft,
  paddingRight: props.$paddingRight,
  display: props.$display,
  position: props.$position,
  width: props.$width,
  height: props.$height,
  justifyContent: props.$justify,
  alignItems: props.$align,
  gap: props.$gap,
  flex: props.$flex,
  flexGrow: props.$flexGrow,
  flexShrink: props.$flexShrink,
  flexDirection: props.$flexDirection,
  minWidth: props.$minWidth,
  maxWidth: props.$maxWidth,
  minHeight: props.$minHeight
})}

  ${({ $isolate }) => $isolate && css`
      isolation: isolate;
    `}

  ${({ $flexWrap }) => $flexWrap && css`
      flex-wrap: wrap;
    `}
  ${({ $reverse }) => $reverse && css`
      flex-direction: row-reverse;
    `}
  ${({ $backgroundColor }) => $backgroundColor && css`
      background-color: ${colors[$backgroundColor]};
    `}
  ${({ $borderColor }) => $borderColor && css`
      box-shadow: inset 0 0 0 1px ${colors[$borderColor]};
    `}

  ${({ $borderRadius }) => $borderRadius && css`
      border-radius: ${radius[$borderRadius]};
    `}
`;
const InternalFlexBox = ({
  children,
  padding,
  paddingX,
  paddingY,
  paddingLeft,
  paddingRight,
  paddingBottom,
  paddingTop,
  margin,
  marginX,
  marginY,
  marginLeft,
  marginRight,
  marginBottom,
  marginTop,
  isolate,
  position,
  justify = "flex-start",
  // default value
  align = "stretch",
  // default value
  gap = "none",
  // default value
  flexWrap = false,
  flexDirection,
  flex,
  width,
  height,
  backgroundColor,
  borderColor,
  borderRadius,
  flexGrow,
  flexShrink,
  minWidth,
  maxWidth,
  minHeight,
  DEPRECATED_onClick,
  ...rest
}, ref) => {
  const resolvedGap = spaceVariantToValue(gap);
  const commonBoxProps = resolveCommonBoxProps({
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingTop,
    margin,
    marginX,
    marginY,
    marginLeft,
    marginRight,
    marginBottom,
    marginTop,
    isolate,
    position,
    width,
    height,
    backgroundColor,
    borderColor,
    borderRadius,
    flexGrow,
    flexShrink,
    minWidth,
    maxWidth,
    minHeight,
    DEPRECATED_onClick
  });
  return /* @__PURE__ */ jsx(
    StyledBox,
    {
      ...commonBoxProps,
      $display: "flex",
      $justify: resolveResponsiveProp(justify),
      $align: resolveResponsiveProp(align),
      $gap: resolvedGap,
      $flexWrap: flexWrap,
      $flex: resolveResponsiveProp(flex == null ? void 0 : flex.toString()),
      $flexDirection: resolveResponsiveProp(
        flexDirection
      ),
      ...rest,
      ref,
      children
    }
  );
};
const FlexBox = forwardRef(InternalFlexBox);
const Box = ({
  children,
  padding,
  paddingX,
  paddingY,
  paddingLeft,
  paddingRight,
  paddingBottom,
  paddingTop,
  margin,
  marginX,
  marginY,
  marginLeft,
  marginRight,
  marginBottom,
  marginTop,
  display,
  isolate,
  position,
  width,
  height,
  backgroundColor,
  borderColor,
  borderRadius,
  flexGrow,
  flexShrink,
  minWidth,
  maxWidth,
  minHeight,
  DEPRECATED_onClick,
  ...rest
}) => {
  const commonBoxProps = resolveCommonBoxProps({
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingTop,
    margin,
    marginX,
    marginY,
    marginLeft,
    marginRight,
    marginBottom,
    marginTop,
    isolate,
    position,
    width,
    height,
    backgroundColor,
    borderColor,
    borderRadius,
    flexGrow,
    flexShrink,
    minWidth,
    maxWidth,
    minHeight,
    DEPRECATED_onClick
  });
  return /* @__PURE__ */ jsx(StyledBox, { ...commonBoxProps, $display: display, ...rest, children });
};
const Wrapper$1 = styled.div`
  background-color: ${colors.surfaceBrand};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
`;
const ContentWrapper$1 = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.surfaceDefault};
  border-radius: ${r8};
`;
const Content = styled.div`
  padding: ${s64};
  padding-top: ${(props) => props.$hasNotification ? 0 : void 0};
  display: flex;
  flex-direction: row;
`;
const Background = styled.div`
  display: flex;
  min-width: 456px;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;
const getWidth = (logoName) => {
  switch (logoName) {
    case SvgImageName.ARDOQ_DISCOVER_LOGO_WITH_NAME:
      return 173;
    default:
      return void 0;
  }
};
const Container = ({
  logoName = SvgImageName.ARDOQ_LOGO_WITH_NAME,
  notification,
  footer,
  children
}) => /* @__PURE__ */ jsxs(Wrapper$1, { children: [
  /* @__PURE__ */ jsx(
    LoadingScreenLogo,
    {
      color: colors.iconBrand,
      width: getWidth(logoName),
      svgImageName: logoName
    }
  ),
  /* @__PURE__ */ jsx(
    FlexBox,
    {
      justify: "center",
      align: "center",
      flex: 4,
      style: { marginTop: "120px" },
      children: /* @__PURE__ */ jsx(Background, { children: /* @__PURE__ */ jsxs(ContentWrapper$1, { children: [
        notification && /* @__PURE__ */ jsx(
          Box,
          {
            marginBottom: "medium",
            padding: "medium",
            style: { width: "463px" },
            children: notification
          }
        ),
        /* @__PURE__ */ jsx(Content, { $hasNotification: !!notification, children })
      ] }) })
    }
  ),
  /* @__PURE__ */ jsx(FlexBox, { flex: 1, children: " " }),
  /* @__PURE__ */ jsx(FlexBox, { paddingY: "small", paddingX: "xlarge", justify: "center", width: "full", children: /* @__PURE__ */ jsx(Text, { color: "textOnInverted", children: footer }) })
] });
const Header$1 = ({ title, subtitle }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(Header1, { children: title }),
  subtitle && /* @__PURE__ */ jsx(Box, { marginTop: "medium", children: /* @__PURE__ */ jsx(Text, { variant: "text1", color: "textSubtle", children: subtitle }) })
] });
const Wrapper = styled.div`
  border-left: 1px solid ${colors.borderAction};
  color: ${colors.textSubtle};
  position: relative;
  margin: -999999px ${s64}; /* This is a hack to show the divider border when we have a notification in the layout. The container should have 'overflow: hidden' to hide the border that falls outside the container. */
`;
const Divider = styled.div`
  position: absolute;
  top: 50%;
  margin-top: -${s24};
  background: ${colors.surfaceDefault};
  padding: ${s16};
  width: ${s64};
  margin-left: -${s32};
  text-align: center;
`;
const DividerBlock = () => /* @__PURE__ */ jsx(Wrapper, { children: /* @__PURE__ */ jsx(Divider, { children: /* @__PURE__ */ jsx(Header5, { children: "OR" }) }) });
const Stack$1 = styled.div`
  display: flex;
  flex-direction: column;
  width: 335px;
`;
const PlainTextContainer = styled.div`
  margin-top: ${s16};
  ${text1};
`;
const FormContainer = styled.div`
  margin-top: ${s32};
  ${text1};
`;
const ActionsContainer = styled.div`
  margin-top: ${s32};
  ${text1};
`;
const FooterContainer = styled.div`
  margin-top: ${s16};
  color: ${colors.textSubtle};
  ${text1};
`;
const BaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  background: ${colors.surfaceStatusNeutral};
`;
const Header = styled.div`
  display: flex;
  align-items: stretch;
  flex: 0 0 58px;
  border-bottom: 1px solid ${colors.borderAction};
  z-index: 10;
  background: ${colors.surfaceDefault};
`;
const NoPrintHeader = styled(Header)`
  @media print {
    display: none !important;
  }
`;
const MainBodyWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  ${(props) => props.$hasHeader && css`
      padding-bottom: 58px;
    `}
`;
const HeaderContent = styled.div`
  padding: 0 32px;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  max-width: ${(props) => props.$fullWidth ? "auto" : "1500px"};
`;
const HeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
`;
const HeaderButtonsContainer = styled.div`
  display: flex;
  button {
    margin-left: 8px;
  }
`;
const BodyWrapper = styled.div`
  background: ${colors.surfaceDefault};
  flex: 1 1 100%;
  overflow: auto;
  height: 100%;
`;
const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 32px;
`;
const SidebarContainer = styled.div`
  display: flex;
  flex: 0 0 270px;
  border-right: 1px solid ${colors.borderAction};
  justify-content: stretch;
  flex-direction: column;
  background: ${colors.surfaceStatusNeutral};
  overflow: auto;
`;
const NoPrintSidebarContainer = styled(SidebarContainer)`
  @media print {
    display: none !important;
  }
`;
const AqHeader = ({
  title,
  buttons,
  fullWidth,
  headerWrapperStyle,
  titleStyle,
  buttonContainerStyle,
  showInPrint = true,
  children
}) => {
  const HeaderElement = showInPrint ? Header : NoPrintHeader;
  return /* @__PURE__ */ jsx(HeaderElement, { children: /* @__PURE__ */ jsxs(HeaderContent, { $fullWidth: fullWidth, style: headerWrapperStyle, children: [
    title && /* @__PURE__ */ jsx(HeaderTitle, { style: titleStyle, children: title }),
    buttons && /* @__PURE__ */ jsx(HeaderButtonsContainer, { style: buttonContainerStyle, children: buttons }),
    children
  ] }) });
};
const BODY_WIDTH_WITH_SIDEBAR = 1250;
const BODY_WIDTH_WITHOUT_SIDEBAR = 1500;
const getBodyWidth = ({
  hasLeftSidebar
}) => hasLeftSidebar ? BODY_WIDTH_WITH_SIDEBAR : BODY_WIDTH_WITHOUT_SIDEBAR;
const AqLayout = forwardRef(
  ({
    children,
    title,
    headerBody,
    renderLeftSidebar,
    renderHeaderButtons,
    bodyWrapperStyle,
    bodyWrapperId,
    bodyContentStyle,
    headerWrapperStyle,
    titleStyle,
    buttonContainerStyle,
    layoutWrapperStyle,
    leftSidebarWrapperStyle,
    fullWidth,
    contextMenu,
    printHeader = true,
    printLeftSidebar = true,
    ...rest
  }, ref) => {
    const hasHeader = Boolean(title) || Boolean(renderHeaderButtons) || Boolean(headerBody);
    const hasLeftSidebar = !!renderLeftSidebar;
    const SidebarElement = printLeftSidebar ? SidebarContainer : NoPrintSidebarContainer;
    return /* @__PURE__ */ jsxs(
      BaseContainer,
      {
        style: layoutWrapperStyle,
        "data-context-menu": contextMenu,
        ref,
        ...rest,
        children: [
          hasHeader && /* @__PURE__ */ jsx(
            AqHeader,
            {
              title,
              buttons: renderHeaderButtons == null ? void 0 : renderHeaderButtons(),
              headerWrapperStyle,
              titleStyle,
              buttonContainerStyle,
              fullWidth,
              showInPrint: printHeader,
              children: headerBody
            }
          ),
          /* @__PURE__ */ jsxs(MainBodyWrapper, { $hasHeader: hasHeader, children: [
            hasLeftSidebar && /* @__PURE__ */ jsx(SidebarElement, { style: leftSidebarWrapperStyle, children: renderLeftSidebar && renderLeftSidebar() }),
            /* @__PURE__ */ jsx(BodyWrapper, { style: bodyWrapperStyle, id: bodyWrapperId, children: /* @__PURE__ */ jsx(
              BodyContent,
              {
                style: {
                  maxWidth: fullWidth ? "auto" : getBodyWidth({ hasLeftSidebar }),
                  ...bodyContentStyle || {}
                },
                children
              }
            ) })
          ] })
        ]
      }
    );
  }
);
const NavItem = styled.div.attrs((props) => ({
  className: props.$active && "active" || props.disabled && "disabled" || ""
}))`
  cursor: pointer;
  padding: 16px 32px;
  color: ${colors.textOnInvertedSubtle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 24px;
  ${(props) => props.$hasError && css`
      padding-right: 16px;
    `}
  &:hover, &.active {
    color: ${colors.textModerate};
    background-color: ${colors.surfaceActionSecondary};
  }
  &.disabled {
    cursor: default;
    color: ${colors.textDisabled};
    &:hover {
      color: ${colors.textDisabled};
      background-color: transparent;
    }
  }
`;
const LOADER_WIDTH = 94;
const LOADER_HEIGHT = 8;
const LoaderBox = styled.div`
  width: ${LOADER_WIDTH}px;
  height: ${LOADER_HEIGHT}px;
  border-radius: ${r4};
  margin-top: ${s16};
  margin-bottom: ${s64};
  position: relative;
  overflow: hidden;
`;
const MovingBar = styled.div`
  @keyframes loader-move {
    from {
      transform: translateX(-100%);
    }

    to {
      transform: translateX(100%);
    }
  }

  background: ${colors.surfaceBrandStrong};
  border-radius: ${r4};
  height: 100%;
  width: 100%;
  animation-duration: 1.5s;
  animation-name: loader-move;
  animation-iteration-count: infinite;
`;
const Loader = () => /* @__PURE__ */ jsx(LoaderBox, { children: /* @__PURE__ */ jsx(MovingBar, {}) });
const WEB_SAFE_FONT = [
  "Trebuchet MS",
  "-apple-system",
  "Segoe UI",
  "Roboto",
  "Noto Sans",
  "Ubuntu",
  "Cantarell",
  "Helvetica Neue"
].join(",");
const PageWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${colors.surfaceBrand};
  color: ${colors.textSubtle};
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const Title = styled.div`
  font-family: ${WEB_SAFE_FONT};
  font-size: 16px;
  font-weight: 400;
  margin-top: ${s64};
  color: ${colors.textModerate};
`;
const TipText = styled.div`
  font-family: ${WEB_SAFE_FONT};
  min-height: ${s64};
  max-width: 530px;
  text-align: center;
`;
const LoadingScreen = ({
  title = "We’re preparing your data...",
  tipText,
  tipTextId
}) => /* @__PURE__ */ jsxs(PageWrapper, { children: [
  /* @__PURE__ */ jsx(LoadingScreenLogo, { color: colors.iconBrand }),
  /* @__PURE__ */ jsxs(ContentWrapper, { children: [
    /* @__PURE__ */ jsx(Title, { children: title }),
    /* @__PURE__ */ jsx(Loader, {}),
    tipTextId && /* @__PURE__ */ jsx(TipText, { id: tipTextId }),
    tipText && /* @__PURE__ */ jsx(TipText, { children: tipText })
  ] })
] });
const OverlayWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  & > img {
    height: 10px;
    margin-bottom: ${s16};
  }
  opacity: 1;
  animation-duration: 1s;
  animation-timing-function: ease-in;
  animation-name: fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
const LoadingOverlay = forwardRef(
  ({ message, hideSpinner, imgUrl, loaderSize, ...divProps }, ref) => /* @__PURE__ */ jsx(OverlayWrapper, { ...divProps, ref, children: imgUrl ? /* @__PURE__ */ jsxs(Fragment, { children: [
    message && /* @__PURE__ */ jsx(Box, { marginBottom: "medium", children: /* @__PURE__ */ jsx(Header4, { color: "textDefault", children: message }) }),
    !hideSpinner && /* @__PURE__ */ jsx("img", { src: imgUrl })
  ] }) : /* @__PURE__ */ jsx(
    ArdoqLoaderComponent,
    {
      size: loaderSize || LoaderSize.LARGE,
      loaderColor: LoaderColor.BRAND,
      loaderText: message,
      hideSpinner
    }
  ) })
);
const Stack = ({
  children,
  justify = "flex-start",
  // default value
  align = "stretch",
  // default value
  gap = "none",
  // default value
  flex,
  ...rest
}) => {
  const resolvedGap = spaceVariantToValue(gap);
  return /* @__PURE__ */ jsx(
    StyledBox,
    {
      $display: "flex",
      $flexDirection: "column",
      $justify: resolveResponsiveProp(justify),
      $align: resolveResponsiveProp(align),
      $gap: resolvedGap,
      $flex: resolveResponsiveProp(flex == null ? void 0 : flex.toString()),
      ...rest,
      children
    }
  );
};
const resolveFlexDirection = (columnBelow) => {
  if (columnBelow === "tablet") {
    return { mobile: "column", tablet: "row" };
  }
  if (columnBelow === "desktop") {
    return { mobile: "column", tablet: "column", desktop: "row" };
  }
  if (columnBelow === "desktopLarge") {
    return {
      mobile: "column",
      tablet: "column",
      desktop: "column",
      desktopLarge: "row"
    };
  }
  if (columnBelow === "desktopXLarge") {
    return {
      mobile: "column",
      tablet: "column",
      desktop: "column",
      desktopLarge: "column",
      desktopXLarge: "row"
    };
  }
  return "row";
};
const Inline = ({
  children,
  justify,
  align,
  gap,
  reverse,
  columnBelow,
  ...rest
}) => {
  const resolvedGap = spaceVariantToValue(gap);
  return /* @__PURE__ */ jsx(
    StyledBox,
    {
      $display: "flex",
      $justify: resolveResponsiveProp(justify),
      $align: resolveResponsiveProp(align),
      $flexDirection: resolveResponsiveProp(
        resolveFlexDirection(columnBelow)
      ),
      $gap: resolvedGap,
      $reverse: reverse,
      $flexWrap: true,
      ...rest,
      children
    }
  );
};
const Hero = {
  Container,
  Stack: Stack$1,
  DividerBlock,
  Header: Header$1,
  PlainTextContainer,
  FormContainer,
  ActionsContainer,
  FooterContainer
};
export {
  AqHeader,
  AqLayout,
  Box,
  FlexBox,
  Hero,
  Inline,
  LoadingOverlay,
  LoadingScreen,
  LoadingScreenLogo,
  NavItem,
  Stack,
  resolveFlexDirection,
  spaceVariantToValue
};
//# sourceMappingURL=index.js.map
